import React from "react";
import { motion } from "framer-motion";
import {
  ExternalLink,
  ArrowUpRight,
  Globe2,
  Code2,
  Calendar,
  Circle,
  Check,
} from "lucide-react";
import { PROJECTS } from "./data";
import { useContent } from "./useContent";
import { resolveFileUrl } from "./fileUrl";

const FeaturedProject = ({ p, index }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`project-card-${p.id}`}
      className="group relative rounded-[28px] overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.01] backdrop-blur-xl"
    >
      {/* Floating accent halo */}
      <div
        className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 group-hover:opacity-70 transition-opacity duration-700"
        style={{ background: `${p.accent}33` }}
      />
      <div
        className="pointer-events-none absolute -bottom-32 -left-32 w-[360px] h-[360px] rounded-full blur-3xl opacity-25"
        style={{ background: `${p.accent}22` }}
      />

      <div className="relative grid lg:grid-cols-[1.15fr_1fr] gap-0">
        {/* LEFT — live site preview */}
        <a
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`project-preview-${p.id}`}
          className="relative block aspect-[16/10] lg:aspect-auto lg:min-h-[560px] overflow-hidden bg-[#0a0b10] group/preview"
        >
          {/* Browser chrome */}
          <div className="absolute top-0 left-0 right-0 z-20 px-5 py-3 flex items-center gap-2 bg-gradient-to-b from-black/70 to-transparent backdrop-blur-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
            <div className="ml-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.06] border border-white/10 text-[10.5px] tracking-[0.15em] uppercase font-mono text-white/55">
              <Globe2 className="w-3 h-3" />
              {p.displayUrl}
            </div>
            <div className="ml-auto text-[10px] tracking-[0.3em] uppercase font-mono text-white/40 hidden md:block">
              Live
            </div>
          </div>

          {/* Screenshot */}
          <img
            src={resolveFileUrl(p.preview)}
            alt={`${p.name} live preview`}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1500ms] ease-out group-hover/preview:scale-[1.04]"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />

          {/* Soft gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-tr from-black/55 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-l from-[#06070b] via-transparent to-transparent opacity-50 hidden lg:block" />

          {/* Hover CTA */}
          <div className="absolute bottom-6 right-6 z-10 flex items-center gap-2 px-4 py-2.5 rounded-full bg-black/55 border border-white/15 backdrop-blur-xl text-[11px] tracking-[0.22em] uppercase text-white font-mono opacity-0 group-hover/preview:opacity-100 translate-y-2 group-hover/preview:translate-y-0 transition-all duration-500">
            Visit site
            <ArrowUpRight className="w-3.5 h-3.5" />
          </div>

          {/* Live indicator */}
          <div className="absolute bottom-6 left-6 z-10 flex items-center gap-2 text-[10.5px] tracking-[0.3em] uppercase font-mono text-emerald-300/90">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
              <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
            </span>
            Production · Live
          </div>
        </a>

        {/* RIGHT — details */}
        <div className="relative p-8 md:p-10 lg:p-12 flex flex-col">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase font-mono" style={{ color: p.accent }}>
            <Code2 className="w-3.5 h-3.5" />
            Selected Work · {String(index + 1).padStart(2, "0")}
          </div>

          <h3
            className="mt-5 text-[34px] md:text-[48px] leading-[1.02] tracking-tight text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {p.name}
          </h3>
          <p className="mt-3 text-white/55 text-[14px] tracking-wide">{p.tagline}</p>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-white/55">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-white/40" />
              <span className="font-mono">{p.year}</span>
            </div>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>{p.role}</span>
          </div>

          <div className="mt-6 h-px bg-white/10" />

          <p className="mt-6 text-[15px] leading-[1.75] text-white/72">{p.mission}</p>

          {/* Impact list */}
          <ul className="mt-6 space-y-2.5">
            {p.impact.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[13.5px] text-white/70">
                <span
                  className="mt-1 w-4 h-4 rounded-full grid place-items-center shrink-0"
                  style={{ background: `${p.accent}1c`, color: p.accent }}
                >
                  <Check className="w-2.5 h-2.5" strokeWidth={3} />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* Stack */}
          <div className="mt-7">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-mono mb-2.5">
              Stack
            </div>
            <div className="flex flex-wrap gap-1.5">
              {p.stack.map((s) => (
                <span
                  key={s}
                  className="text-[10.5px] tracking-[0.12em] uppercase font-mono px-2.5 py-1 rounded-full border border-white/10 text-white/65 bg-white/[0.03]"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-auto pt-8 flex flex-wrap items-center gap-3">
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`project-visit-${p.id}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-medium text-black text-[12px] tracking-[0.22em] uppercase transition-transform hover:scale-[1.03]"
              style={{ background: p.accent }}
            >
              Visit Live Site
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white/75 hover:text-white border border-white/15 hover:border-white/30 text-[11px] tracking-[0.22em] uppercase font-mono transition-colors"
            >
              {p.displayUrl}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

const Projects = () => {
  const items = useContent("projects", PROJECTS);
  return (
    <section id="projects" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">
            Built · Shipped · Live
          </div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2
              className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Selected <span className="italic">work</span>.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              Real products, in production, doing real work in the world. A small but considered selection of things I&rsquo;ve designed, built, and shipped end-to-end.
            </p>
          </div>
        </motion.div>

        <div className="space-y-8">
          {items.map((p, i) => (
            <FeaturedProject key={p.id} p={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
